import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Warehouse, Eye, EyeSlash, CircleNotch, Translate } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const HERO_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/912750ef-fe82-4662-9a3e-72ea98f1362e/images/24c24cdb2df5a2661a31212f831b1ba7ca7742400a14f91a694c40962127552a.png';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex relative">
      {/* Language toggle */}
      <button
        onClick={toggleLanguage}
        data-testid="login-language-toggle"
        className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-black/40 hover:bg-black/60 transition-all rounded-lg text-sm font-medium"
        style={{ color: 'var(--dm-text-muted)' }}
      >
        <Translate size={16} />
        <span>{language === 'en' ? 'Language - Hindi' : 'Language - English'}</span>
      </button>

      {/* Left - Hero Image */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden"
        style={{ background: 'var(--dm-bg-deep)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ background: `radial-gradient(circle at 30% 50%, var(--dm-accent), transparent 70%)` }} />
        <div className="relative z-10 text-center max-w-md">
          <img
            src={HERO_IMAGE}
            alt="DukaanMate Inventory Management"
            className="w-72 h-72 object-contain mx-auto mb-8 rounded-3xl"
            style={{ filter: 'drop-shadow(0 0 40px rgba(0,0,0,0.3))' }}
          />
          <h2 className="text-3xl font-bold text-[var(--text)] mb-3" style={{ fontFamily: 'Outfit' }}>
            {t('appTagline')}
          </h2>
          <p className="text-lg" style={{ color: 'var(--dm-text-muted)' }}>
            Manage your stock, create invoices, and track everything in one place.
          </p>
        </div>
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-black mb-4 rounded-2xl">
              <Warehouse size={40} weight="bold" style={{ color: 'var(--dm-accent)' }} />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>
              Dukaan<span style={{ color: 'var(--dm-accent)' }}>Mate</span>
            </h1>
            <p className="mt-2 lg:hidden" style={{ color: 'var(--dm-text-muted)' }}>{t('appTagline')}</p>
          </div>

          {/* Login Form Card */}
          <div className="p-8 rounded-2xl" style={{ background: 'var(--dm-bg-card)', border: `1px solid var(--dm-border)` }}>
            <h2 className="text-xl font-semibold text-[var(--text)] mb-6" style={{ fontFamily: 'Outfit' }}>
              {t('signIn')}
            </h2>

            {error && (
              <div className="mb-4 p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm rounded-lg" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>
                  {t('emailAddress')}
                </Label>
                <Input
                  id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailAddress')} required data-testid="login-email"
                  className="text-[var(--text)] h-12 rounded-xl"
                  style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }}
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-sm mb-2 block" style={{ color: 'var(--dm-text-muted)' }}>
                  {t('password')}
                </Label>
                <div className="relative">
                  <Input
                    id="password" type={showPassword ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder={t('password')} required
                    data-testid="login-password"
                    className="text-white h-12 rounded-xl pr-12"
                    style={{ background: 'var(--dm-input-bg)', borderColor: 'var(--dm-border)' }}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 hover:text-white" style={{ color: 'var(--dm-text-muted)' }}>
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading} data-testid="login-submit"
                className="w-full h-12 bg-black text-white font-semibold hover:bg-black/80 transition-all duration-150 rounded-xl disabled:opacity-50"
                style={{ border: `1px solid var(--dm-border)` }}>
                {loading ? <CircleNotch size={20} className="animate-spin" /> : t('signIn')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
